import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Check, Sparkles, Zap, Crown, Loader2, Video, Star } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { usePageT } from '../i18n/usePageT';
import { SEO } from '../components/SEO';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const PricingPage = () => {
  const { token, isAuthenticated, isVendor } = useAuth();
  const { language } = useLanguage();
  const T = usePageT();

  const [plans, setPlans] = useState([]);
  const [addons, setAddons] = useState([]);
  const [mySub, setMySub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subscribing, setSubscribing] = useState(null);

  useEffect(() => {
    const fetch = async () => {
      try {
        const [p, m] = await Promise.all([
          axios.get(`${API}/subscriptions/plans`),
          isVendor && token ? axios.get(`${API}/subscriptions/me`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => null) : Promise.resolve(null),
        ]);
        setPlans(p.data.plans || []);
        setAddons(p.data.video_addons || []);
        if (m?.data) setMySub(m.data);
      } catch (_) { /* silent */ } finally { setLoading(false); }
    };
    fetch();
  }, [token, isVendor]);

  const subscribe = async (planId, addonId) => {
    if (!isAuthenticated) {
      toast.info(T('Connectez-vous pour souscrire', 'Sign in to subscribe'));
      return;
    }
    if (!isVendor) {
      toast.info(T('Devenez vendeur d\'abord pour souscrire à un plan.', 'Become a seller first to subscribe to a plan.'));
      return;
    }
    setSubscribing(planId || addonId);
    try {
      const params = new URLSearchParams();
      if (planId) params.set('plan_id', planId);
      if (addonId !== undefined) params.set('addon_id', addonId || '');
      await axios.post(`${API}/subscriptions/subscribe?${params.toString()}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      const r = await axios.get(`${API}/subscriptions/me`, { headers: { Authorization: `Bearer ${token}` } });
      setMySub(r.data);
      toast.success(T('Abonnement mis à jour !', 'Subscription updated!'));
    } catch (err) {
      toast.error(err.response?.data?.detail || T('Erreur', 'Error'));
    } finally {
      setSubscribing(null);
    }
  };

  const features = (plan) => {
    const fr = plan.features_fr || [];
    const en = plan.features_en || [];
    if (language === 'fr') return fr;
    return fr.map((f, i) => T(f, en[i]));
  };

  if (loading) {
    return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#0F4C5C]" /></div>;
  }

  return (
    <div className="min-h-screen bg-[#FBF8F3]" data-testid="pricing-page">
      <section className="bg-gradient-to-br from-[#0F4C5C] via-[#0F4C5C] to-[#0B3A47] text-white py-16 md:py-24 relative overflow-hidden">
        <SEO
          title={T('Tarifs vendeurs', 'Seller pricing')}
          description={T(
            'Vendez librement, gardez le contrôle. Sans engagement, vous changez ou résiliez à tout moment.',
            'Sell freely, stay in control. No commitment, switch or cancel anytime.'
          )}
          url={`${window.location.origin}/pricing`}
        />
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle at 30% 30%, #E9C46A 0%, transparent 40%), radial-gradient(circle at 70% 70%, #2A9D8F 0%, transparent 40%)' }} />
        <div className="container-main relative text-center">
          <p className="text-xs uppercase tracking-[0.4em] text-[#E9C46A] font-semibold mb-4">{T('Tarifs vendeurs', 'Seller pricing')}</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-4">
            {T('Choisissez votre plan', 'Choose your plan')}
            <span className="text-[#E9C46A]">.</span>
          </h1>
          <p className="text-lg text-white/75 max-w-2xl mx-auto">
            {T(
              'Vendez librement, gardez le contrôle. Sans engagement, vous changez ou résiliez à tout moment.',
              'Sell freely, stay in control. No commitment, switch or cancel anytime.'
            )}
          </p>
        </div>
      </section>

      {/* Plans grid */}
      <section className="container-main -mt-16 relative">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
          {plans.map((plan) => {
            const isMine = mySub?.plan?.id === plan.id;
            return (
              <div
                key={plan.id}
                className={`relative bg-white rounded-3xl border p-8 transition-all ${
                  plan.highlight
                    ? 'border-[#E9C46A] shadow-2xl shadow-[#E9C46A]/10 md:scale-[1.02]'
                    : 'border-[#0F4C5C]/10 shadow-lg'
                }`}
                data-testid={`plan-card-${plan.id}`}
              >
                {plan.highlight && (
                  <span className="absolute -top-3 right-6 bg-[#E9C46A] text-[#0F4C5C] text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full">
                    {T('Le plus populaire', 'Most popular')}
                  </span>
                )}
                <div className="flex items-center gap-3 mb-2">
                  {plan.id === 'pro' ? <Crown className="w-6 h-6 text-[#E9C46A]" /> : <Zap className="w-6 h-6 text-[#2A9D8F]" />}
                  <h2 className="text-2xl font-bold tracking-tight text-[#0F4C5C]">{plan.name}</h2>
                </div>
                <p className="text-sm text-[#0F4C5C]/65">{T(plan.tagline, plan.tagline_en)}</p>

                <div className="mt-6 mb-6">
                  <div className="flex items-baseline gap-1">
                    <span className="text-5xl font-bold text-[#0F4C5C] tracking-tight">
                      {plan.monthly_eur === 0 ? (T('Gratuit', 'Free')) : `${plan.monthly_eur}€`}
                    </span>
                    {plan.monthly_eur > 0 && <span className="text-[#0F4C5C]/60">{T('/mois', '/month')}</span>}
                  </div>
                  <p className="text-sm font-semibold text-[#2A9D8F] mt-2">
                    {T('Commission : {pct}% par vente', 'Commission: {pct}% per sale').replace('{pct}', plan.commission_pct)}
                  </p>
                </div>

                <ul className="space-y-3 mb-8">
                  {features(plan).map((f, i) => (
                    <li key={i} className="flex items-start gap-2.5">
                      <Check className="w-5 h-5 text-[#2A9D8F] flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-[#0F4C5C]">{f}</span>
                    </li>
                  ))}
                </ul>

                <Button
                  onClick={() => subscribe(plan.id)}
                  disabled={isMine || subscribing === plan.id}
                  className={`w-full h-12 text-base font-semibold ${
                    plan.highlight ? 'bg-[#0F4C5C] hover:bg-[#0F4C5C]/90 text-white' : 'bg-white border-2 border-[#0F4C5C] text-[#0F4C5C] hover:bg-[#FBF8F3]'
                  }`}
                  data-testid={`subscribe-${plan.id}`}
                >
                  {subscribing === plan.id ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  {isMine ? T('Votre plan actuel', 'Your current plan') : T('Choisir ce plan', 'Choose this plan')}
                </Button>
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
};
